import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  CheckCircle2,
  Circle,
  Trash2,
} from "lucide-react";
import {
  generateId,
  useApp,
  type CardCondition,
  type CardRarity,
} from "@/context/AppContext";
import { generateTemplateBundles } from "@/features/bundleTemplateEngine";
import {
  BUNDLE_SELECTION_PROFILES,
  BUNDLE_SELECTION_PROFILE_HINTS,
  BUNDLE_SELECTION_PROFILE_LABELS,
  TEMPLATE_INTENT_CHOICES,
  type BundleSelectionProfile,
  type BundleTemplate,
  type BundleTemplateFilters,
  type CardSupertype,
  type StudioCategory,
} from "@/types/bundleTemplate";
import { ManagerOnly } from "@/components/ManagerOnly";

export const Route = createFileRoute("/tools/bundle-template-edit")({
  component: () => (
    <ManagerOnly>
      <BundleTemplateEditPage />
    </ManagerOnly>
  ),
  validateSearch: (search): { id?: string } => ({
    id: typeof search.id === "string" ? search.id : undefined,
  }),
});

const RARITY_CHOICES: CardRarity[] = [
  "Common",
  "Uncommon",
  "Rare",
  "Promo",
  "Trainer",
  "Energy",
];

const CONDITION_CHOICES: CardCondition[] = ["NM", "LP", "MP", "HP", "Damaged"];
const SUPERTYPE_CHOICES: CardSupertype[] = ["Pokemon", "Trainer", "Energy"];
const GAME_CHOICES: { value: string; label: string }[] = [
  { value: "Pokemon", label: "Pokemon" },
  { value: "Magic", label: "Magic" },
  { value: "Yu-Gi-Oh", label: "Yu-Gi-Oh" },
  { value: "Other", label: "Other" },
  { value: "", label: "Any" },
];

function BundleTemplateEditPage() {
  const navigate = useNavigate();
  const params = Route.useSearch();
  const {
    cards,
    bundleTemplates,
    addBundleTemplate,
    updateBundleTemplate,
    deleteBundleTemplate,
  } = useApp();

  const editing = useMemo(
    () => bundleTemplates.find((t) => t.id === params.id) ?? null,
    [bundleTemplates, params.id],
  );

  const [name, setName] = useState(editing?.name ?? "");
  const [intent, setIntent] = useState<Exclude<StudioCategory, "All">>(
    editing?.intent ?? "Random",
  );
  const [targetCount, setTargetCount] = useState<string>(
    String(editing?.targetCount ?? 50),
  );
  const [filters, setFilters] = useState<BundleTemplateFilters>(
    editing?.filters ?? {},
  );
  const [gameInput, setGameInput] = useState(editing?.filters.game ?? "");
  const [maxPriceInput, setMaxPriceInput] = useState(
    editing?.filters.maxPricePerCard != null
      ? String(editing.filters.maxPricePerCard)
      : "",
  );
  const [selectionProfile, setSelectionProfile] =
    useState<BundleSelectionProfile>(
      editing?.selection?.profile ?? "top-value",
    );

  // If the route loaded before AppContext rehydrated, sync once when editing resolves.
  useEffect(() => {
    if (editing && name === "" && targetCount === "50") {
      setName(editing.name);
      setIntent(editing.intent);
      setTargetCount(String(editing.targetCount));
      setFilters(editing.filters);
      setGameInput(editing.filters.game ?? "");
      setMaxPriceInput(
        editing.filters.maxPricePerCard != null
          ? String(editing.filters.maxPricePerCard)
          : "",
      );
      setSelectionProfile(editing.selection?.profile ?? "top-value");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.id]);

  const distinctSets = useMemo(() => {
    const seen = new Set<string>();
    for (const c of cards) if (c.setName) seen.add(c.setName);
    return [...seen].sort();
  }, [cards]);

  const distinctTypes = useMemo(() => {
    const seen = new Set<string>();
    for (const c of cards) for (const t of c.tcgTypes ?? []) seen.add(t);
    return [...seen].sort();
  }, [cards]);

  const distinctSeries = useMemo(() => {
    const seen = new Set<string>();
    for (const c of cards) if (c.tcgSeries) seen.add(c.tcgSeries);
    return [...seen].sort();
  }, [cards]);

  const targetNum = Number.parseInt(targetCount, 10) || 0;

  const preview = useMemo(() => {
    const trimmedName = name.trim() || "Preview";
    const filtersForPreview: BundleTemplateFilters = {
      ...filters,
      game: gameInput.trim() || undefined,
      maxPricePerCard: maxPriceInput.trim()
        ? Number.parseFloat(maxPriceInput)
        : undefined,
    };
    const tmpl: BundleTemplate = {
      id: editing?.id ?? "preview",
      name: trimmedName,
      intent,
      targetCount: targetNum,
      filters: filtersForPreview,
      selection: { profile: selectionProfile },
      createdAt: editing?.createdAt ?? new Date().toISOString(),
    };
    const unlisted = cards.filter(
      (c) => c.listingStatus === "Unlisted" && c.quantity > 0,
    );
    const result = generateTemplateBundles(unlisted, [tmpl], new Set());
    const first = result[0];
    return {
      cardCount: first?.cardCount ?? 0,
      availabilityPct: first?.availabilityPct ?? 0,
      hasMatch: !!first,
    };
  }, [
    cards,
    intent,
    targetNum,
    filters,
    gameInput,
    maxPriceInput,
    name,
    selectionProfile,
    editing?.id,
    editing?.createdAt,
  ]);

  const isValid = name.trim().length > 0 && targetNum > 0;

  const toggleArrayValue = <K extends keyof BundleTemplateFilters>(
    key: K,
    value: string,
  ) => {
    setFilters((prev) => {
      const current = (prev[key] as string[] | undefined) ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return {
        ...prev,
        [key]: next.length ? next : undefined,
      } as BundleTemplateFilters;
    });
  };

  const handleSave = () => {
    if (!isValid) return;
    const cleanedFilters: BundleTemplateFilters = {
      ...filters,
      game: gameInput.trim() || undefined,
      maxPricePerCard: maxPriceInput.trim()
        ? Number.parseFloat(maxPriceInput)
        : undefined,
    };
    if (editing) {
      updateBundleTemplate(editing.id, {
        name: name.trim(),
        intent,
        targetCount: targetNum,
        filters: cleanedFilters,
        selection: { profile: selectionProfile },
      });
    } else {
      addBundleTemplate({
        id: generateId("BTPL"),
        name: name.trim(),
        intent,
        targetCount: targetNum,
        filters: cleanedFilters,
        selection: { profile: selectionProfile },
        createdAt: new Date().toISOString(),
      });
    }
    navigate({ to: "/tools/bundle-templates" });
  };

  const handleDelete = () => {
    if (!editing) return;
    deleteBundleTemplate(editing.id);
    navigate({ to: "/tools/bundle-templates" });
  };

  const PreviewIcon =
    preview.availabilityPct >= 100
      ? CheckCircle2
      : preview.availabilityPct >= 80
        ? Circle
        : AlertCircle;
  const previewToneClass =
    preview.availabilityPct >= 100
      ? "border-emerald-500 bg-emerald-500/10 text-emerald-700"
      : preview.availabilityPct >= 80
        ? "border-amber-500 bg-amber-500/10 text-amber-700"
        : "border-border bg-muted text-foreground";

  return (
    <section className="flex h-full flex-col">
      <div className="border-b border-border pb-3">
        <Link
          to="/tools/bundle-templates"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Cancel
        </Link>
      </div>

      <div className="flex-1 space-y-3.5 overflow-y-auto py-4 pr-2">
        <h1 className="text-xl font-bold text-foreground">
          {editing ? "Edit template" : "New bundle template"}
        </h1>

        {/* Preview card */}
        <div
          className={`flex items-center gap-3 rounded-md border p-3 ${previewToneClass}`}
        >
          <PreviewIcon className="h-4 w-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold">
              {preview.hasMatch
                ? `${preview.availabilityPct}% available`
                : "No matching inventory"}
            </div>
            <div className="text-xs opacity-80">
              {preview.cardCount} of {targetNum || 0} cards match these filters
              in current inventory.
            </div>
          </div>
        </div>

        {/* Name */}
        <Field label="NAME">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. 100-Card Water Pokémon Lot"
            className="w-full rounded-md bg-muted px-3 py-2 text-sm font-medium text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </Field>

        {/* Intent */}
        <Field label="INTENT">
          <ChipRow>
            {TEMPLATE_INTENT_CHOICES.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                active={intent === cat}
                onClick={() => setIntent(cat)}
              />
            ))}
          </ChipRow>
        </Field>

        {/* Target count + stepper */}
        <Field label={`TARGET CARD COUNT (${targetNum})`}>
          <div className="flex items-center gap-1.5">
            <StepperBtn
              label="−10"
              onClick={() =>
                setTargetCount(String(Math.max(1, targetNum - 10)))
              }
            />
            <StepperBtn
              label="−1"
              onClick={() => setTargetCount(String(Math.max(1, targetNum - 1)))}
            />
            <input
              type="text"
              inputMode="numeric"
              value={targetCount}
              onChange={(e) =>
                setTargetCount(e.target.value.replace(/[^0-9]/g, ""))
              }
              className="flex-1 rounded-md bg-muted px-3 py-2 text-center text-base font-bold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <StepperBtn
              label="+1"
              onClick={() => setTargetCount(String(targetNum + 1))}
            />
            <StepperBtn
              label="+10"
              onClick={() => setTargetCount(String(targetNum + 10))}
            />
          </div>
        </Field>

        {/* Selection profile */}
        <Field label="SELECTION PROFILE">
          <ChipRow>
            {BUNDLE_SELECTION_PROFILES.map((p) => (
              <Chip
                key={p}
                label={BUNDLE_SELECTION_PROFILE_LABELS[p]}
                active={selectionProfile === p}
                onClick={() => setSelectionProfile(p)}
              />
            ))}
          </ChipRow>
          <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
            {BUNDLE_SELECTION_PROFILE_HINTS[selectionProfile]}
          </p>
        </Field>

        {/* Game */}
        <Field label="GAME">
          <ChipRow>
            {GAME_CHOICES.map((g) => (
              <Chip
                key={g.label}
                label={g.label}
                active={(gameInput || "") === g.value}
                onClick={() => setGameInput(g.value)}
              />
            ))}
          </ChipRow>
        </Field>

        {/* Supertype */}
        <Field label="SUPERTYPE">
          <ChipRow>
            {SUPERTYPE_CHOICES.map((s) => (
              <Chip
                key={s}
                label={s}
                active={(filters.supertypes ?? []).includes(s)}
                onClick={() => toggleArrayValue("supertypes", s)}
              />
            ))}
          </ChipRow>
        </Field>

        {/* Rarity */}
        <Field label="RARITY">
          <ChipRow>
            {RARITY_CHOICES.map((r) => (
              <Chip
                key={r}
                label={r}
                active={(filters.rarities ?? []).includes(r)}
                onClick={() => toggleArrayValue("rarities", r)}
              />
            ))}
          </ChipRow>
        </Field>

        {/* Conditions */}
        <Field label="CONDITIONS">
          <ChipRow>
            {CONDITION_CHOICES.map((c) => (
              <Chip
                key={c}
                label={c}
                active={(filters.conditions ?? []).includes(c)}
                onClick={() => toggleArrayValue("conditions", c)}
              />
            ))}
          </ChipRow>
        </Field>

        {distinctTypes.length > 0 && (
          <Field label="TYPES (POKÉMON)">
            <ChipRow>
              {distinctTypes.map((t) => (
                <Chip
                  key={t}
                  label={t}
                  active={(filters.types ?? []).includes(t)}
                  onClick={() => toggleArrayValue("types", t)}
                />
              ))}
            </ChipRow>
          </Field>
        )}

        {distinctSets.length > 0 && (
          <Field label={`SETS (${(filters.sets ?? []).length} selected)`}>
            <ChipRow>
              {distinctSets.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  active={(filters.sets ?? []).includes(s)}
                  onClick={() => toggleArrayValue("sets", s)}
                />
              ))}
            </ChipRow>
          </Field>
        )}

        {distinctSeries.length > 0 && (
          <Field label="SERIES">
            <ChipRow>
              {distinctSeries.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  active={(filters.series ?? []).includes(s)}
                  onClick={() => toggleArrayValue("series", s)}
                />
              ))}
            </ChipRow>
          </Field>
        )}

        <Field label="MAX MARKET PRICE PER CARD (USD)">
          <input
            type="text"
            inputMode="decimal"
            value={maxPriceInput}
            onChange={(e) =>
              setMaxPriceInput(e.target.value.replace(/[^0-9.]/g, ""))
            }
            placeholder="No cap"
            className="w-full rounded-md bg-muted px-3 py-2 text-sm font-medium text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </Field>

        <div className="flex gap-2.5 pt-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={!isValid}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2.5 text-sm font-bold text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            <Check className="h-4 w-4" />
            {editing ? "Save changes" : "Create template"}
          </button>
          {editing && (
            <button
              type="button"
              onClick={handleDelete}
              className="flex items-center justify-center gap-1.5 rounded-md bg-destructive px-4 py-2.5 text-sm font-bold text-destructive-foreground hover:opacity-90"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}

function ChipRow({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap gap-1.5">{children}</div>;
}

function Chip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
        active
          ? "bg-primary text-primary-foreground"
          : "bg-muted text-foreground hover:bg-muted/80"
      }`}
    >
      {label}
    </button>
  );
}

function StepperBtn({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="min-w-[40px] rounded-md bg-muted px-3 py-2 text-sm font-semibold text-foreground hover:bg-muted/80"
    >
      {label}
    </button>
  );
}
